import {
  conversationHistoryMutationResponseSchema,
  conversationHistoryResponseSchema,
} from "@/features/chat-history/conversationHistorySchemas";
import { baseApi } from "@/shared/api/baseApi";

export type ConversationHistoryView = "active" | "archived";
export type ConversationHistoryOperation =
  | "archive"
  | "pin"
  | "rename"
  | "restore"
  | "unpin";

export interface ConversationHistoryScope {
  organizationId: string;
  query: string;
  shopBindingId: string;
  userId: string;
  view: ConversationHistoryView;
}

interface ConversationHistoryPageParam {
  cursor: string;
}

export interface MutateConversationHistoryInput {
  conversationId: string;
  expectedRevision: number;
  operation: ConversationHistoryOperation;
  organizationId: string;
  shopBindingId: string;
  title?: string;
  userId: string;
}

function conversationHistoryPath(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}/conversation-history`;
}

function conversationHistoryPageUrl(
  input: ConversationHistoryScope,
  page: ConversationHistoryPageParam,
) {
  const params = new URLSearchParams({ limit: "30", view: input.view });
  params.set("shop_binding_id", input.shopBindingId);
  if (input.query) params.set("query", input.query);
  if (page.cursor) params.set("cursor", page.cursor);
  return `${conversationHistoryPath(input.organizationId)}?${params.toString()}`;
}

export const conversationHistoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversationHistory: builder.infiniteQuery<
      ReturnType<typeof conversationHistoryResponseSchema.parse>,
      ConversationHistoryScope,
      ConversationHistoryPageParam
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) =>
          lastPage.nextCursor ? { cursor: lastPage.nextCursor } : undefined,
        initialPageParam: { cursor: "" },
      },
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: `${input.userId}:${input.organizationId}:${input.shopBindingId}`, type: "Conversations" },
      ],
      query: ({ pageParam, queryArg }) =>
        conversationHistoryPageUrl(queryArg, pageParam),
      transformResponse: (response) =>
        conversationHistoryResponseSchema.parse(response),
    }),
    mutateConversationHistory: builder.mutation<
      ReturnType<typeof conversationHistoryMutationResponseSchema.parse>,
      MutateConversationHistoryInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "Conversation" },
        { id: `${input.userId}:${input.organizationId}:${input.shopBindingId}`, type: "Conversations" },
      ],
      query: ({
        conversationId,
        expectedRevision,
        operation,
        organizationId,
        shopBindingId,
        title,
      }) => ({
        body: {
          expected_revision: expectedRevision,
          operation,
          ...(title === undefined ? {} : { title }),
        },
        method: "PATCH",
        url: `${conversationHistoryPath(organizationId)}/${encodeURIComponent(conversationId)}?${new URLSearchParams({ shop_binding_id: shopBindingId }).toString()}`,
      }),
      transformResponse: (response) =>
        conversationHistoryMutationResponseSchema.parse(response),
    }),
  }),
});

export const {
  useGetConversationHistoryInfiniteQuery,
  useMutateConversationHistoryMutation,
} = conversationHistoryApi;
